import type { ArchiveStats, Folder, PageHighlights, PageView, SearchParams, SearchResponse } from './types';
import { authDisabled, endpoints } from '../config/endpoints';
import { store } from '../store';
import { logout, refreshSession } from '../store/authSlice';
import type { AuthSession } from '../store/authSession';

export const apiBase = endpoints.archiveApi;
let refreshRequest: Promise<AuthSession | null> | null = null;

function tokenExpired(token: string) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(window.atob(payload)) as { exp?: number };
    return typeof decoded.exp !== 'number' || decoded.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

async function accessToken() {
  const session = store.getState().auth;
  if (session.accessToken && !tokenExpired(session.accessToken)) return session.accessToken;
  if (!session.refreshToken) return null;

  if (!refreshRequest) {
    refreshRequest = store.dispatch(refreshSession()).unwrap().catch(() => null).finally(() => { refreshRequest = null; });
  }

  const refreshed = await refreshRequest;
  return refreshed?.accessToken ?? null;
}

async function send(path: string, init?: RequestInit, isPublic = false): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!isPublic && !authDisabled) {
    const token = await accessToken();
    if (!token) {
      store.dispatch(logout());
      window.location.assign('/login');
      throw new Error('Sitzung abgelaufen');
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${apiBase}${path}`, { ...init, headers });
  if (response.status === 401 && !isPublic) {
    store.dispatch(logout());
    window.location.assign('/login');
    throw new Error('Sitzung abgelaufen');
  }
  // 403 is not a session problem: the token is good and this account simply has
  // no archive. Sending the user back to the login would loop forever.
  if (response.status === 403) {
    throw new Error('Für dieses Konto ist kein Archiv freigeschaltet.');
  }
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message.trim() || response.statusText);
  }
  return response;
}

async function request<T>(path: string, init?: RequestInit, isPublic = false): Promise<T> {
  const response = await send(path, init, isPublic);
  return response.json() as Promise<T>;
}

export const archiveApi = {
  stats: () => request<ArchiveStats>('/api/stats'),
  folders: () => request<{ folders: Folder[] }>('/api/folders').then((r) => r.folders),
  search: (query: string, options: SearchParams, signal?: AbortSignal) => {
    const params = new URLSearchParams({ q: query });
    // Only what is actually set: an empty parameter is not the same as an absent
    // one to a reader of the URL, and these URLs get shared.
    for (const [key, value] of Object.entries(options)) {
      if (value) params.set(key, String(value));
    }
    return request<SearchResponse>(`/api/search?${params.toString()}`, { signal });
  },
  page: (pdfId: number, page: number, signal?: AbortSignal) =>
    request<PageView>(`/api/pdf/${pdfId}/page/${page}`, { signal }),
  highlights: (pdfId: number, page: number, query: string, signal?: AbortSignal) =>
    request<PageHighlights>(
      `/api/pdf/${pdfId}/page/${page}/highlights?q=${encodeURIComponent(query)}`, { signal }),
  /**
   * Page images only. The source document is deliberately not reachable — the
   * archive is read page by page and the original is never handed out.
   *
   * Fetched rather than pointed at with an `<img src>`, because an img element
   * cannot carry an Authorization header and the image route is gated like every
   * other one. The caller owns the returned object URL and must revoke it.
   * Caching is not lost: the response is immutable and content-addressed, so the
   * browser's HTTP cache still answers the second fetch.
   */
  pageImage: async (pdfId: number, page: number, signal?: AbortSignal) => {
    const response = await send(`/api/pdf/${pdfId}/page/${page}/image`, { signal });
    return URL.createObjectURL(await response.blob());
  }
};
